import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import ImageCropModal from './ImageCropModal';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  Upload, 
  Palette, 
  Image, 
  Settings, 
  Package,
  Star,
  FileImage,
  Paintbrush,
  Eye,
  EyeOff,
  TrendingUp,
  Users,
  DollarSign,
  ShoppingBag
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shared cache key with App.js so admin edits refresh the pre-React theme
// bootstrap for the whole site.
const SITE_SETTINGS_CACHE_KEY = 'hbe_site_settings_v1';

/** Admin money display: 1,000.00 style (en-US) */
const formatMoney = (n) =>
  Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const readCachedSettings = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SITE_SETTINGS_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const writeCachedSettings = (settings) => {
  if (typeof window === 'undefined' || !settings) return;
  try {
    window.localStorage.setItem(SITE_SETTINGS_CACHE_KEY, JSON.stringify(settings));
    const r = document.documentElement.style;
    if (settings.primary_color) r.setProperty('--hbe-primary', settings.primary_color);
    if (settings.secondary_color) r.setProperty('--hbe-secondary', settings.secondary_color);
    if (settings.accent_color) r.setProperty('--hbe-accent', settings.accent_color);
    if (settings.text_color) r.setProperty('--hbe-text', settings.text_color);
    if (settings.border_color) r.setProperty('--hbe-border', settings.border_color);
    if (settings.background_type && settings.background_value) {
      if (settings.background_type === 'image') {
        r.setProperty('--hbe-bg', `url('${settings.background_value}') center/cover no-repeat fixed`);
      } else {
        r.setProperty('--hbe-bg', settings.background_value);
      }
    }
  } catch { /* ignore */ }
};

const COUNTRIES = [
  'Canada', 'United States', 'Germany', 'United Kingdom', 'France', 
  'Japan', 'China', 'Australia', 'Switzerland', 'Mexico'
];

// Canonical denomination order requested by the admin for the autocomplete list.
const CANONICAL_DENOMINATIONS = ['$1', '$2', '$5', '$10', '$20', '$50', '$100'];

// Reusable autocomplete input used for Title / Grade / Denomination fields
// in the admin product form. Suggestions are filtered by substring match as
// the user types and refined on every keystroke.
function AutocompleteInput({
  id,
  value,
  onChange,
  suggestions,
  placeholder,
  required,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef(null);

  const q = (value || '').toString().toLowerCase().trim();
  const matches = (q
    ? suggestions.filter((s) => s && s.toString().toLowerCase().includes(q))
    : suggestions
  ).slice(0, 12);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    setHighlight(0);
  }, [value]);

  const pick = (s) => {
    onChange(s);
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <Input
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || matches.length === 0) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, matches.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            pick(matches[highlight]);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        data-testid={testId}
      />
      {open && matches.length > 0 && (
        <div
          className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-amber-300 rounded-md shadow-lg max-h-48 overflow-y-auto"
          data-testid={testId ? `${testId}-suggestions` : undefined}
        >
          {matches.map((s, i) => (
            <button
              key={`${s}-${i}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-1.5 text-sm ${
                i === highlight ? 'bg-amber-100' : 'hover:bg-amber-50'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminDashboard() {
  const [products, setProducts] = useState([]);
  const [grades, setGrades] = useState([]);
  const [siteSettings, setSiteSettings] = useState(readCachedSettings());
  const [categoryImages, setCategoryImages] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]); // dynamic categories
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDesc, setNewCategoryDesc] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [editingCategoryDesc, setEditingCategoryDesc] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [cropQueue, setCropQueue] = useState([]);        // File[]
  const [cropTarget, setCropTarget] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [adminCols, setAdminCols] = useState(2); // 1, 2, or 3 wide
  const [silverHoldings, setSilverHoldings] = useState([]);
  const [silverSummary, setSilverSummary] = useState(null);
  const [silverLoading, setSilverLoading] = useState(false);
  const [silverForm, setSilverForm] = useState({
    description: '', silver_oz: '', year: '', price_paid: '', price_sold: '', notes: ''
  });
  const [editingSilverId, setEditingSilverId] = useState(null);

  const [adminSortFilter, setAdminSortFilter] = useState('oldest');
  const [adminCategoryFilter, setAdminCategoryFilter] = useState('all');
  // Justin 2026-10-08: product search on the admin Products tab.
  const [adminProductSearch, setAdminProductSearch] = useState('');
  const [showBulkPaidDialog, setShowBulkPaidDialog] = useState(false);
  const [bulkPaidValues, setBulkPaidValues] = useState({}); // { productId: string }
  const [bulkPriceValues, setBulkPriceValues] = useState({}); // { productId: string }
  const [savingBulkPaid, setSavingBulkPaid] = useState(false);
  const [bulkPaidSearch, setBulkPaidSearch] = useState('');

  // Suggestion pools derived from existing products in the DB.
  const titleSuggestions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of products) {
      const t = (p?.title || '').trim();
      if (t && !seen.has(t.toLowerCase())) {
        seen.add(t.toLowerCase());
        out.push(t);
      }
    }
    return out.sort((a, b) => a.localeCompare(b));
  }, [products]);

  const gradeSuggestions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of products) {
      const g = (p?.grade || '').trim();
      if (g && !seen.has(g.toLowerCase())) {
        seen.add(g.toLowerCase());
        out.push(g);
      }
    }
    return out.sort((a, b) => a.localeCompare(b));
  }, [products]);

  const denominationSuggestions = useMemo(() => {
    const seen = new Set();
    const out = [];
    // Canonical order first: $1, $2, $5, $10, $20, $50, $100.
    for (const d of CANONICAL_DENOMINATIONS) {
      seen.add(d.toLowerCase());
      out.push(d);
    }
    // Then any other denominations already in the DB, in insertion order.
    for (const p of products) {
      const d = (p?.denomination || '').trim();
      if (d && !seen.has(d.toLowerCase())) {
        seen.add(d.toLowerCase());
        out.push(d);
      }
    }
    return out;
  }, [products]);

  // Country suggestions: seed list first, then any custom countries already on products.
  // Free-text AutocompleteInput lets Sir type countries that are not in the list.
  const countrySuggestions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of COUNTRIES) {
      const t = (c || '').trim();
      if (t && !seen.has(t.toLowerCase())) {
        seen.add(t.toLowerCase());
        out.push(t);
      }
    }
    for (const p of products) {
      const c = (p?.country || '').trim();
      if (c && !seen.has(c.toLowerCase())) {
        seen.add(c.toLowerCase());
        out.push(c);
      }
    }
    return out;
  }, [products]);

  const displayedAdminProducts = useMemo(() => {
    let list = [...products];
    if (adminCategoryFilter && adminCategoryFilter !== 'all') {
      list = list.filter(p => (p.category || '') === adminCategoryFilter);
    }
    // Justin 2026-10-08: admin product search across title, description,
    // grade, denomination, year, category, country (same fields as storefront).
    const adminQ = adminProductSearch.trim().toLowerCase();
    if (adminQ) {
      list = list.filter(p => {
        const haystack = [p.title, p.description, p.grade, p.denomination, p.year, p.category, p.country]
          .filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(adminQ);
      });
    }
    if (adminSortFilter === 'available') list = list.filter(p => !p.is_sold);
    else if (adminSortFilter === 'sold') list = list.filter(p => p.is_sold);
    else if (adminSortFilter === 'featured') list = list.filter(p => p.is_featured === true);
    const time = (p) => new Date(p.created_at || 0).getTime();
    const yearNum = (p) => {
      const n = parseInt(p?.year, 10);
      return Number.isFinite(n) ? n : null;
    };
    if (adminSortFilter === 'newest') {
      // Sort numerically by the bill's year (descending). Items without a year go to the very bottom.
      list.sort((a, b) => {
        const ya = yearNum(a);
        const yb = yearNum(b);
        if (ya === null && yb === null) return 0;
        if (ya === null) return 1;
        if (yb === null) return -1;
        return yb - ya;
      });
    }
    else if (adminSortFilter === 'oldest') {
      // Sort numerically by the bill's year (ascending). Items without a year go to the bottom.
      list.sort((a, b) => {
        const ya = yearNum(a);
        const yb = yearNum(b);
        if (ya === null && yb === null) return 0;
        if (ya === null) return 1;
        if (yb === null) return -1;
        return ya - yb;
      });
    }
    else if (adminSortFilter === 'featured') {
      list.sort((a, b) => {
        const ao = a.featured_order;
        const bo = b.featured_order;
        const aHas = ao !== null && ao !== undefined && ao !== '';
        const bHas = bo !== null && bo !== undefined && bo !== '';
        if (aHas && bHas) return Number(ao) - Number(bo);
        if (aHas) return -1;
        if (bHas) return 1;
        return time(b) - time(a);
      });
    }
    else if (adminSortFilter === 'paid-low') list.sort((a, b) => (a.paid_price ?? Infinity) - (b.paid_price ?? Infinity));
    else if (adminSortFilter === 'paid-high') list.sort((a, b) => (b.paid_price ?? -Infinity) - (a.paid_price ?? -Infinity));
    return list;
  }, [products, adminSortFilter, adminCategoryFilter, adminProductSearch]);
  const [stats, setStats] = useState({ totalProducts: 0, availableProducts: 0, totalGrades: 0, totalPaid: 0, totalRevenue: 0, totalProfit: 0 });

  const [productForm, setProductForm] = useState({
    title: '',
    description: '', 
    category: '',
    price: '',
    paid_price: '',
    discount_percentage: '',
    image_url: '',
    image_urls: [],
    grade: '',
    year: '',
    denomination: '',
    country: 'Canada',
    is_available: true,
    allow_offers: false,
    min_offer_amount: '',
    is_featured: false,
    featured_order: '',
    image_rotation: 0,
    is_display_only: false,
    is_sold: false,
  });

  const [settingsForm, setSettingsForm] = useState({
    primary_color: '#d97706',
    secondary_color: '#b45309', 
    accent_color: '#f59e0b',
    text_color: '#92400e',
    border_color: '#fbbf24',
    background_type: 'gradient',
    background_value: '#F3EEE4',
    background_overlay: true,
    hero_image_url: '',
    logo_url: '',
    site_title: 'Historical Bill Exchange',
    site_subtitle: 'Premium Collectible Currency',
    hero_tagline: '',
    side_banner_left_url: '',
    side_banner_right_url: '',
    side_banner_fade: true,
    announcement_text: '',
    announcement_enabled: true,
    // Gradient settings
    gradient_direction: '135deg',
    gradient_color1: '#F3EEE4',
    gradient_color2: '#fff',
    gradient_color3: '#F7F4EF',
    background_solid_color: '#ffffff'
  });

  const [newGrade, setNewGrade] = useState({
    grade: '',
    description: '',
    category_type: 'bills'
  });

  useEffect(() => {
    fetchProducts();
    fetchGrades();
    fetchSiteSettings();
    fetchCategoryImages();
    fetchCategoriesList();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`);
      setProducts(response.data);
      const items = response.data;
      const totalPaid = items.reduce((sum, p) => sum + (p.paid_price ? Number(p.paid_price) : 0), 0);
      const soldItems = items.filter(p => p.is_sold);
      const totalRevenue = soldItems.reduce((sum, p) => sum + (p.price ? Number(p.price) : 0), 0);
      const totalProfit = soldItems.reduce((sum, p) => {
        if (p.paid_price == null) return sum;
        return sum + (Number(p.price || 0) - Number(p.paid_price));
      }, 0);
      setStats(prev => ({
        ...prev, 
        totalProducts: items.length,
        availableProducts: items.filter(p => p.is_available).length,
        totalPaid,
        totalRevenue,
        totalProfit,
      }));
    } catch (error) {
      toast.error('Error loading products');
      console.error('Products error:', error);
    }
  };

  const fetchGrades = async () => {
    try {
      const response = await axios.get(`${API}/grades`);
      setGrades(response.data);
      setStats(prev => ({ ...prev, totalGrades: response.data.length }));
    } catch (error) {
      toast.error('Error loading grades');
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSiteSettings(response.data);
      writeCachedSettings(response.data);
      
      // Parse gradient if it exists
      let gradientDirection = '135deg';
      let gradientColor1 = '#fef7e0';
      let gradientColor2 = '#fff';
      let gradientColor3 = '#fff7ed';
      let backgroundSolidColor = '#ffffff';

      if (response.data.background_value) {
        if (response.data.background_type === 'gradient') {
          const gradientMatch = response.data.background_value.match(/linear-gradient\(([^,]+),\s*([^,\s]+)[^,]*,\s*([^,\s]+)[^,]*,\s*([^,\s]+)/);
          if (gradientMatch) {
            gradientDirection = gradientMatch[1].trim();
            gradientColor1 = gradientMatch[2].trim();
            gradientColor2 = gradientMatch[3].trim();
            gradientColor3 = gradientMatch[4].trim();
          }
        } else if (response.data.background_type === 'solid') {
          backgroundSolidColor = response.data.background_value;
        }
      }

      setSettingsForm({
        primary_color: response.data.primary_color,
        secondary_color: response.data.secondary_color,
        accent_color: response.data.accent_color,
        text_color: response.data.text_color || '#92400e',
        border_color: response.data.border_color || '#fbbf24',
        background_type: response.data.background_type,
        background_value: response.data.background_value,
        background_overlay: response.data.background_overlay ?? true,
        hero_image_url: response.data.hero_image_url,
        logo_url: response.data.logo_url || '',
        site_title: response.data.site_title || 'Historical Bill Exchange',
        site_subtitle: response.data.site_subtitle || 'Premium Collectible Currency',
        hero_tagline: response.data.hero_tagline ?? '',
        side_banner_left_url: response.data.side_banner_left_url ?? '',
        side_banner_right_url: response.data.side_banner_right_url ?? '',
        side_banner_fade: response.data.side_banner_fade ?? true,
        announcement_text: (response.data.announcement_text && String(response.data.announcement_text).trim())
          ? response.data.announcement_text
          : 'Authenticated notes — Graded by PMG / PCGS / BCS — Secure checkout — Canadian dealer — Email Historic.Bills@gmail.com with any inquiries',
        announcement_enabled: response.data.announcement_enabled !== false,
        gradient_direction: gradientDirection,
        gradient_color1: gradientColor1,
        gradient_color2: gradientColor2,
        gradient_color3: gradientColor3,
        background_solid_color: backgroundSolidColor
      });
    } catch (error) {
      toast.error('Error loading settings');
    }
  };


  const fetchSilver = async () => {
    setSilverLoading(true);
    try {
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API}/admin/silver`),
        axios.get(`${API}/admin/silver/summary`),
      ]);
      setSilverHoldings(listRes.data || []);
      setSilverSummary(sumRes.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error loading silver holdings');
    } finally {
      setSilverLoading(false);
    }
  };

  const resetSilverForm = () => {
    setEditingSilverId(null);
    setSilverForm({ description: '', silver_oz: '', year: '', price_paid: '', price_sold: '', notes: '' });
  };

  const handleSilverSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      description: silverForm.description.trim(),
      silver_oz: parseFloat(silverForm.silver_oz) || 0,
      year: silverForm.year.trim() || null,
      weight: null,
      price_paid: parseFloat(silverForm.price_paid) || 0,
      price_sold: silverForm.price_sold === '' || silverForm.price_sold == null
        ? null
        : parseFloat(silverForm.price_sold),
      notes: silverForm.notes.trim() || null,
    };
    try {
      if (editingSilverId) {
        await axios.put(`${API}/admin/silver/${editingSilverId}`, payload);
        toast.success('Silver holding updated');
      } else {
        await axios.post(`${API}/admin/silver`, payload);
        toast.success('Silver holding added');
      }
      resetSilverForm();
      fetchSilver();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save silver holding');
    }
  };

  const handleEditSilver = (row) => {
    setEditingSilverId(row.id);
    setSilverForm({
      description: row.description || '',
      silver_oz: row.silver_oz != null ? String(row.silver_oz) : '',
      year: row.year || '',
      price_paid: row.price_paid != null ? String(row.price_paid) : '',
      price_sold: row.price_sold != null ? String(row.price_sold) : '',
      notes: row.notes || '',
    });
  };

  const handleDeleteSilver = async (id) => {
    if (!window.confirm('Delete this silver holding?')) return;
    try {
      await axios.delete(`${API}/admin/silver/${id}`);
      toast.success('Deleted');
      fetchSilver();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const fetchCategoryImages = async () => {
    try {
      const response = await axios.get(`${API}/category-images`);
      setCategoryImages(response.data);
    } catch (error) {
      toast.error('Error loading category images');
    }
  };

  const fetchCategoriesList = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategoriesList(response.data || []);
    } catch (error) {
      toast.error('Error loading categories');
    }
  };

  const handleAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return toast.error('Category name required');
    try {
      await axios.post(`${API}/admin/categories`, { name, description: newCategoryDesc.trim() });
      toast.success('Category added');
      setNewCategoryName('');
      setNewCategoryDesc('');
      fetchCategoriesList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add category');
    }
  };

  const handleSaveCategoryEdit = async () => {
    if (!editingCategoryId) return;
    try {
      await axios.put(`${API}/admin/categories/${editingCategoryId}`, {
        name: editingCategoryName.trim(),
        description: editingCategoryDesc.trim(),
      });
      toast.success('Category updated');
      setEditingCategoryId(null);
      fetchCategoriesList();
      fetchProducts();
      fetchCategoryImages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    }
  };

  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"? Products keep their category label but this entry will disappear from the site.`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${id}`);
      toast.success('Category deleted');
      fetchCategoriesList();
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  const handleReorderCategory = async (index, direction) => {
    const list = [...categoriesList];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    setCategoriesList(list);
    try {
      await axios.post(`${API}/admin/categories/reorder`, { ordered_ids: list.map(c => c.id) });
    } catch (e) {
      toast.error('Reorder failed');
      fetchCategoriesList();
    }
  };

  const addImageToProduct = (imageUrl) => {
    setProductForm(prev => ({
      ...prev,
      image_urls: [...prev.image_urls, imageUrl]
    }));
  };

  const removeImageFromProduct = (indexToRemove) => {
    setProductForm(prev => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, index) => index !== indexToRemove)
    }));
  };

  // Numbered image slots: 0 = main/primary (image_url), 1+ = image_urls[n-1]
  const getImageAtSlot = (form, slotIndex) => {
    if (slotIndex === 0) return form.image_url || '';
    return (form.image_urls && form.image_urls[slotIndex - 1]) || '';
  };

  const clearImageSlot = (slotIndex) => {
    setProductForm(prev => {
      if (slotIndex === 0) return { ...prev, image_url: '' };
      return {
        ...prev,
        image_urls: (prev.image_urls || []).filter((_, index) => index !== slotIndex - 1),
      };
    });
  };

  const applyUrlsToSlots = (prev, startSlot, urls) => {
    let primary = prev.image_url || '';
    let extras = [...(prev.image_urls || [])];
    urls.forEach((url, i) => {
      const slot = startSlot + i;
      if (slot === 0) {
        primary = url;
      } else {
        const idx = slot - 1;
        if (idx < extras.length) extras[idx] = url;
        else extras.push(url);
      }
    });
    return { ...prev, image_url: primary, image_urls: extras };
  };

  const uploadOneBlob = async (blob, fileName) => {
    const formData = new FormData();
    const finalName = fileName || 'upload.jpg';
    formData.append('file', blob, finalName);
    const response = await axios.post(`${API}/admin/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Accept': 'application/json',
      },
    });
    // Store as RELATIVE path so images work on any domain (preview / production / custom)
    return response.data.file_url;
  };

  const handleImageUpload = async (file, callback) => {
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WebP image file');
      return;
    }
    if (file.size > maxSize) {
      toast.error('Image file must be less than 10MB');
      return;
    }
    setUploadingImage(true);
    try {
      const url = await uploadOneBlob(file, file.name);
      callback(url);
      toast.success('Image uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Open the crop modal for a queue of files (multi-select supported)
  const openCropForFiles = (target, fileList) => {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024;
    const files = Array.from(fileList || []).filter((f) => {
      if (!allowed.includes(f.type)) { toast.error(`Skipped ${f.name}: unsupported type`); return false; }
      if (f.size > maxSize) { toast.error(`Skipped ${f.name}: over 10MB`); return false; }
      return true;
    });
    if (files.length === 0) return;
    setCropTarget(target);
    setCropQueue(files);
  };

  // Called by the crop modal when the user finishes cropping all images.
  const handleCropFinished = async (blobs) => {
    const target = cropTarget;
    setCropQueue([]);
    setCropTarget(null);
    if (!blobs || blobs.length === 0) return;
    setUploadingImage(true);
    setUploadProgress({ current: 0, total: blobs.length });
    const urls = [];
    let ok = 0;
    for (let i = 0; i < blobs.length; i++) {
      setUploadProgress({ current: i + 1, total: blobs.length });
      try {
        const url = await uploadOneBlob(blobs[i].blob, blobs[i].name);
        urls.push(url);
        ok += 1;
      } catch (e) {
        toast.error(`Failed to upload ${blobs[i].name}`);
      }
    }
    setUploadingImage(false);
    setUploadProgress({ current: 0, total: 0 });

    // Apply the uploaded URLs based on target
    if (target === 'primary') {
      // First becomes primary; the rest become additional images
      if (urls.length > 0) setProductForm(prev => ({ ...prev, image_url: urls[0], image_urls: [...prev.image_urls, ...urls.slice(1)] }));
    } else if (target === 'additional') {
      setProductForm(prev => ({ ...prev, image_urls: [...prev.image_urls, ...urls] }));
    } else if (typeof target === 'string' && target.startsWith('slot:')) {
      const startSlot = parseInt(target.slice(5), 10) || 0;
      if (urls.length > 0) setProductForm(prev => applyUrlsToSlots(prev, startSlot, urls));
    } else if (target === 'logo') {
      if (urls[0]) setSettingsForm(prev => ({ ...prev, logo_url: urls[0] }));
    } else if (target === 'hero') {
      if (urls[0]) setSettingsForm(prev => ({ ...prev, hero_image_url: urls[0] }));
    } else if (target === 'background') {
      if (urls[0]) setSettingsForm(prev => ({ ...prev, background_value: urls[0] }));
    } else if (target === 'side_left') {
      if (urls[0]) setSettingsForm(prev => ({ ...prev, side_banner_left_url: urls[0] }));
    } else if (target === 'side_right') {
      if (urls[0]) setSettingsForm(prev => ({ ...prev, side_banner_right_url: urls[0] }));
    } else if (target && target.startsWith('category:')) {
      const catId = target.slice('category:'.length);
      if (urls[0]) {
        try {
          await axios.put(`${API}/admin/category-image`, null, { params: { category: catId, image_url: urls[0] } });
          toast.success('Category image updated');
          fetchCategoryImages();
        } catch (e) {
          toast.error('Failed to save category image');
        }
      }
    }
    if (ok > 0) toast.success(`${ok} image${ok > 1 ? 's' : ''} uploaded`);
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();

    if (productForm.allow_offers === true) {
      const minVal = parseFloat(productForm.min_offer_amount);
      if (!minVal || minVal <= 0) {
        toast.error('Set a minimum offer amount (greater than $0) before allowing offers.');
        return;
      }
    }

    const productData = {
      ...productForm,
      price: parseFloat(productForm.price),
      paid_price: productForm.paid_price !== '' && productForm.paid_price != null ? parseFloat(productForm.paid_price) : null,
      allow_offers: !!productForm.allow_offers,
      min_offer_amount: productForm.min_offer_amount !== '' && productForm.min_offer_amount != null ? parseFloat(productForm.min_offer_amount) : null,
      is_featured: !!productForm.is_featured,
      featured_order: productForm.is_featured && productForm.featured_order !== '' && productForm.featured_order != null
        ? parseInt(productForm.featured_order, 10)
        : null,
      image_rotation: Number.isFinite(parseFloat(productForm.image_rotation)) ? parseFloat(productForm.image_rotation) : 0,
      is_display_only: !!productForm.is_display_only,
      is_sold: !!productForm.is_sold,
      discount_percentage: parseFloat(productForm.discount_percentage || 0),
      year: productForm.year ? parseInt(productForm.year) : null,
      grade: productForm.grade === 'none' ? '' : productForm.grade
    };

    try {
      if (selectedProduct) {
        await axios.put(`${API}/products/${selectedProduct.id}`, productData);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, productData);
        toast.success('Product created successfully');
      }
      
      fetchProducts();
      resetProductForm();
      setIsAddingProduct(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Error deleting product');
    }
  };

  const handleToggleAvailability = async (productId, currentStatus) => {
    try {
      await axios.put(`${API}/products/${productId}`, { is_available: !currentStatus });
      toast.success(`Product ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchProducts();
    } catch (error) {
      toast.error('Error updating product availability');
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    
    // Generate background value based on type
    let finalSettingsForm = { ...settingsForm };
    
    if (settingsForm.background_type === 'gradient') {
      finalSettingsForm.background_value = `linear-gradient(${settingsForm.gradient_direction}, ${settingsForm.gradient_color1} 0%, ${settingsForm.gradient_color2} 50%, ${settingsForm.gradient_color3} 100%)`;
    } else if (settingsForm.background_type === 'solid') {
      finalSettingsForm.background_value = settingsForm.background_solid_color;
    }
    
    try {
      await axios.put(`${API}/admin/settings`, finalSettingsForm);
      toast.success('Settings updated successfully - refresh page to see changes');
      fetchSiteSettings();
    } catch (error) {
      toast.error('Error updating settings');
    }
  };

  const handleCategoryImageUpdate = async (category, imageUrl) => {
    try {
      console.log('Updating category image:', category, 'with URL:', imageUrl);
      await axios.put(`${API}/admin/category-image`, null, {
        params: { category, image_url: imageUrl }
      });
      toast.success('Category image updated successfully');
      // Force refresh the category images
      await fetchCategoryImages();
      
      // Force a page refresh after a small delay to ensure images load
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error updating category image:', error);
      toast.error('Error updating category image');
    }
  };

  const handleGradeSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/admin/grades`, newGrade);
      toast.success('Grade added successfully');
      fetchGrades();
      setNewGrade({ grade: '', description: '', category_type: 'bills' });
    } catch (error) {
      toast.error('Error adding grade');
    }
  };

  const handleDeleteGrade = async (gradeId) => {
    if (!window.confirm('Are you sure you want to delete this grade?')) return;
    
    try {
      await axios.delete(`${API}/admin/grades/${gradeId}`);
      toast.success('Grade deleted successfully');
      fetchGrades();
    } catch (error) {
      toast.error('Error deleting grade');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      title: '',
      description: '',
      category: '',
      price: '',
      paid_price: '',
      discount_percentage: '',
      image_url: '',
      image_urls: [],
      grade: '',
      year: '',
      denomination: '',
      country: 'Canada',
      is_available: true,
      allow_offers: false,
      min_offer_amount: '',
      is_featured: false,
      featured_order: '',
      image_rotation: 0,
      is_display_only: false,
      is_sold: false,
    });
    setSelectedProduct(null);
  };

  const editProduct = (product) => {
    try {
      console.log('Editing product:', product);
      setProductForm({
        title: product?.title || '',
        description: product?.description || '',
        category: product?.category || '',
        price: product?.price?.toString() || '',
        paid_price: (product?.paid_price != null ? product.paid_price.toString() : ''),
        discount_percentage: product?.discount_percentage?.toString() || '0',
        image_url: product?.image_url || '',
        image_urls: product?.image_urls || [],
        grade: product?.grade || 'none',
        year: product?.year?.toString() || '',
        denomination: product?.denomination || '',
        country: product?.country || 'Canada',
        is_available: product?.is_available !== false,
        allow_offers: product?.allow_offers === true,
        min_offer_amount: (product?.min_offer_amount != null ? product.min_offer_amount.toString() : ''),
        is_featured: product?.is_featured === true,
        featured_order: (product?.featured_order != null ? product.featured_order.toString() : ''),
        image_rotation: Number.isFinite(parseFloat(product?.image_rotation)) ? parseFloat(product.image_rotation) : 0,
        is_display_only: product?.is_display_only === true,
        is_sold: product?.is_sold === true,
      });
      setSelectedProduct(product);
      setIsAddingProduct(true);
    } catch (error) {
      console.error('Error in editProduct:', error);
      toast.error('Error opening product editor');
    }
  };

  const getCategoryImage = (category) => {
    const categoryImg = categoryImages.find(img => img.category === category);
    const imageUrl = categoryImg?.image_url || 'https://images.unsplash.com/photo-1580519542036-c47de6196ba5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHx8MTc1ODM0MDgzMXww&ixlib=rb-4.1.0&q=85';
    
    // Add cache-busting parameter for uploaded images to force refresh
    if (imageUrl.includes('/uploads/')) {
      return `${imageUrl}?v=${Date.now()}`;
    }
    
    return imageUrl;
  };

  return (
    <div 
      className="min-h-screen p-3 sm:p-6"
      style={{
        background: siteSettings?.background_type === 'image' && siteSettings?.background_value
          ? `url(${siteSettings.background_value}) center/cover fixed`
          : siteSettings?.background_type === 'gradient'
          ? siteSettings.background_value
          : (siteSettings?.background_type === 'solid' || siteSettings?.background_type === 'color') && siteSettings?.background_value
          ? siteSettings.background_value
          : '#F3EEE4'
      }}
    >
      {/* Overlay for background images */}
      {siteSettings?.background_overlay && siteSettings?.background_type === 'image' && (
        <div 
          className="absolute inset-0 z-0"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0.9) 100%)'
          }}
        ></div>
      )}
      
      <div className="max-w-[1680px] mx-auto relative z-10">
        <div className="mb-8">
          <h1 
            className="text-4xl font-bold mb-2 font-serif"
            style={{ color: siteSettings?.text_color || '#92400e' }}
          >
            Admin Dashboard
          </h1>
          <p style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.8 }}>
            Manage your {siteSettings?.site_title || 'Historical Bill Exchange'} website
          </p>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mt-6">
            <Card 
              className="bg-white/90 backdrop-blur-sm"
              style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p 
                      className="text-sm"
                      style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.8 }}
                    >
                      Total Products
                    </p>
                    <p 
                      className="text-2xl font-bold"
                      style={{ color: siteSettings?.text_color || '#92400e' }}
                    >
                      {stats.totalProducts}
                    </p>
                  </div>
                  <ShoppingBag 
                    className="h-8 w-8"
                    style={{ color: siteSettings?.primary_color || '#d97706' }}
                  />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="bg-white/90 backdrop-blur-sm"
              style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p 
                      className="text-sm"
                      style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.8 }}
                    >
                      Available Products
                    </p>
                    <p 
                      className="text-2xl font-bold"
                      style={{ color: siteSettings?.text_color || '#92400e' }}
                    >
                      {stats.availableProducts}
                    </p>
                  </div>
                  <Eye 
                    className="h-8 w-8"
                    style={{ color: siteSettings?.primary_color || '#d97706' }}
                  />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="bg-white/90 backdrop-blur-sm"
              style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}
              data-testid="admin-stat-total-paid"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.8 }}>Total Paid</p>
                    <p className="text-2xl font-bold truncate" style={{ color: siteSettings?.text_color || '#92400e' }}>
                      ${formatMoney(stats.totalPaid)}
                    </p>
                    <p className="text-[10px] mt-0.5" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.55 }}>Cost of inventory</p>
                  </div>
                  <DollarSign className="h-8 w-8 shrink-0" style={{ color: siteSettings?.primary_color || '#d97706' }} />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="bg-white/90 backdrop-blur-sm"
              style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}
              data-testid="admin-stat-total-revenue"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.8 }}>Total Revenue</p>
                    <p className="text-2xl font-bold truncate" style={{ color: siteSettings?.text_color || '#92400e' }}>
                      ${formatMoney(stats.totalRevenue)}
                    </p>
                    <p className="text-[10px] mt-0.5" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.55 }}>From sold items</p>
                  </div>
                  <TrendingUp className="h-8 w-8 shrink-0" style={{ color: siteSettings?.primary_color || '#d97706' }} />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="bg-white/90 backdrop-blur-sm"
              style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}
              data-testid="admin-stat-total-profit"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.8 }}>Total Profit</p>
                    <p className="text-2xl font-bold truncate" style={{ color: stats.totalProfit >= 0 ? '#16a34a' : '#dc2626' }}>
                      ${formatMoney(stats.totalProfit)}
                    </p>
                    <p className="text-[10px] mt-0.5" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.55 }}>Sold price − paid</p>
                  </div>
                  <TrendingUp className="h-8 w-8 shrink-0" style={{ color: stats.totalProfit >= 0 ? '#16a34a' : '#dc2626' }} />
                </div>
              </CardContent>
            </Card>
            {/* Available Grades card removed */}
          </div>
        </div>

        <Tabs defaultValue="products" className="space-y-6">
          <TabsList 
            className="grid w-full grid-cols-2 sm:grid-cols-4 bg-white/90 backdrop-blur-sm h-auto"
            style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}
          >
            <TabsTrigger 
              value="products" 
              className="flex items-center justify-center gap-1 sm:gap-2 px-1 py-2 text-xs sm:text-sm"
              style={{ 
                color: siteSettings?.text_color || '#92400e',
                '--primary-color': siteSettings?.primary_color || '#d97706'
              }}
            >
              <Package className="h-4 w-4 shrink-0" />
              <span>Products</span>
            </TabsTrigger>
            <TabsTrigger 
              value="appearance" 
              className="flex items-center justify-center gap-1 sm:gap-2 px-1 py-2 text-xs sm:text-sm"
              style={{ 
                color: siteSettings?.text_color || '#92400e',
                '--primary-color': siteSettings?.primary_color || '#d97706'
              }}
            >
              <Palette className="h-4 w-4 shrink-0" />
              <span>Appearance</span>
            </TabsTrigger>
            <TabsTrigger 
              value="categories" 
              className="flex items-center justify-center gap-1 sm:gap-2 px-1 py-2 text-xs sm:text-sm"
              style={{ 
                color: siteSettings?.text_color || '#92400e',
                '--primary-color': siteSettings?.primary_color || '#d97706'
              }}
            >
              <Image className="h-4 w-4 shrink-0" />
              <span>Categories</span>
            </TabsTrigger>
            <TabsTrigger 
              value="silver" 
              className="font-medium text-xs sm:text-sm py-2"
              style={{ color: siteSettings?.text_color || '#92400e' }}
              data-testid="admin-tab-silver"
              onClick={() => fetchSilver()}
            >
              Silver
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
              <h2 
                className="text-2xl font-bold font-serif"
                style={{ color: siteSettings?.text_color || '#92400e' }}
              >
                Product Management
              </h2>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <div className="flex gap-1 items-center">
                  <span className="text-xs mr-1" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.7 }}>View:</span>
                  {[1, 2, 3].map((n) => {
                    const isActive = adminCols === n;
                    const primary = siteSettings?.primary_color || '#d97706';
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setAdminCols(n)}
                        aria-label={`${n} column${n > 1 ? 's' : ''}`}
                        data-testid={`admin-view-cols-${n}`}
                        className="h-8 w-8 rounded-md border-2 flex items-center justify-center text-xs font-semibold"
                        style={{
                          backgroundColor: isActive ? primary : '#ffffff',
                          borderColor: primary,
                          color: isActive ? '#ffffff' : primary,
                        }}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
                <Button 
                  onClick={() => setIsAddingProduct(true)}
                  className="text-white w-full sm:w-auto"
                  style={{ 
                    backgroundColor: siteSettings?.primary_color || '#d97706',
                    borderColor: siteSettings?.primary_color || '#d97706'
                  }}
                  data-testid="admin-add-product-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    const seedPaid = {};
                    const seedPrice = {};
                    products.forEach(p => {
                      seedPaid[p.id] = p.paid_price != null ? String(p.paid_price) : '';
                      seedPrice[p.id] = p.price != null ? String(p.price) : '';
                    });
                    setBulkPaidValues(seedPaid);
                    setBulkPriceValues(seedPrice);
                    setShowBulkPaidDialog(true);
                  }}
                  className="w-full sm:w-auto"
                  style={{
                    borderColor: siteSettings?.primary_color || '#d97706',
                    color: siteSettings?.primary_color || '#d97706',
                  }}
                  data-testid="admin-bulk-paid-btn"
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Bulk Edit Prices
                </Button>
              </div>
            </div>

            {/* Justin 2026-10-08: product search on the admin Products tab */}
            <div className="relative w-full sm:max-w-md">
              <input
                type="search"
                value={adminProductSearch}
                onChange={(e) => setAdminProductSearch(e.target.value)}
                placeholder="Search products by title, year, denomination…"
                aria-label="Search admin products"
                data-testid="admin-product-search"
                className="w-full pl-9 pr-3 py-2 rounded-lg border-2 bg-white/90 text-sm focus:outline-none focus:ring-2"
                style={{
                  borderColor: siteSettings?.primary_color || '#d97706',
                  color: siteSettings?.text_color || '#92400e',
                }}
              />
              <svg
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                fill="none"
                stroke={siteSettings?.primary_color || '#d97706'}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <div className="flex flex-col gap-2" data-testid="admin-category-filter">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium" style={{ color: siteSettings?.text_color || '#92400e' }}>
                  Category:
                </span>
                {(() => {
                  const primary = siteSettings?.primary_color || '#d97706';
                  const fromList = (categoriesList || []).map(c => c.name).filter(Boolean);
                  const fromProducts = Array.from(new Set((products || []).map(p => p.category).filter(Boolean)));
                  const names = [];
                  fromList.forEach(n => { if (!names.includes(n)) names.push(n); });
                  fromProducts.forEach(n => { if (!names.includes(n)) names.push(n); });
                  const options = [{ id: 'all', label: 'All' }, ...names.map(n => ({ id: n, label: n }))];
                  return options.map(opt => {
                    const isActive = adminCategoryFilter === opt.id;
                    const count = opt.id === 'all'
                      ? products.length
                      : products.filter(p => (p.category || '') === opt.id).length;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setAdminCategoryFilter(opt.id)}
                        data-testid={`admin-category-filter-${opt.id === 'all' ? 'all' : opt.id.replace(/\s+/g, '-').toLowerCase()}`}
                        className="px-3 py-1.5 rounded-full border-2 text-xs sm:text-sm font-medium whitespace-nowrap"
                        style={{
                          backgroundColor: isActive ? primary : '#ffffff',
                          borderColor: primary,
                          color: isActive ? '#ffffff' : primary,
                        }}
                      >
                        {opt.label} ({count})
                      </button>
                    );
                  });
                })()}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label
                htmlFor="admin-sort-filter"
                className="text-sm font-medium"
                style={{ color: siteSettings?.text_color || '#92400e' }}
              >
                Sort & Filter:
              </label>
              <div className="relative w-full sm:w-64">
                <select
                  id="admin-sort-filter"
                  value={adminSortFilter}
                  onChange={(e) => setAdminSortFilter(e.target.value)}
                  data-testid="admin-sort-filter-select"
                  className="w-full appearance-none rounded-lg border-2 bg-white/90 backdrop-blur-sm px-3 py-2 pr-9 text-sm font-medium shadow-sm focus:outline-none focus:ring-2"
                  style={{
                    borderColor: siteSettings?.primary_color || '#d97706',
                    color: siteSettings?.text_color || '#92400e',
                  }}
                >
                  <option value="newest">Year: Newest First</option>
                  <option value="oldest">Year: Oldest First</option>
                  <option value="featured">Featured Only</option>
                  <option value="paid-low">Paid: Low to High</option>
                  <option value="paid-high">Paid: High to Low</option>
                  <option value="available">Available Only</option>
                  <option value="sold">Sold Only</option>
                </select>
                <svg
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4"
                  fill="none"
                  stroke={siteSettings?.primary_color || '#d97706'}
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <span className="text-xs sm:ml-auto" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.7 }}>
                {displayedAdminProducts.length} {displayedAdminProducts.length === 1 ? 'result' : 'results'}
              </span>
            </div>

            <div className={`admin-product-grid grid gap-3 sm:gap-6 ${
              adminCols === 1 ? 'grid-cols-1' : adminCols === 2 ? 'grid-cols-2' : 'grid-cols-3'
            }`}>
              {displayedAdminProducts.map(product => {
                const allImages = [product.image_url, ...(product.image_urls || [])].filter(url => url);
                return (
                  <Card 
                    key={product.id} 
                    className="bg-white/90 backdrop-blur-sm"
                    style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}
                  >
                    <div className="px-2 pt-2 pb-1.5 flex flex-col gap-1 bg-white border-b min-h-[52px]" style={{ borderColor: siteSettings?.border_color || '#D9D2C5' }}>
                      <div className="flex items-center justify-end gap-1">
                        {product.grade ? (
                          <span className="hbe-grade-badge text-[10px] max-w-[90%] truncate">{product.grade}</span>
                        ) : <span className="h-4" aria-hidden="true" />}
                      </div>
                      <div className="flex items-center justify-between gap-1 min-h-[22px]">
                        <div>
                          {product.is_display_only && (
                            <Badge className="bg-blue-600 text-white font-bold shadow text-[10px] px-1.5 py-0">Display Only</Badge>
                          )}
                        </div>
                        <div>
                          {product.is_sold && !product.is_display_only && (
                            <Badge className="bg-red-600 text-white font-bold shadow text-[10px] px-1.5 py-0" data-testid={`admin-sold-badge-${product.id}`}>SOLD</Badge>
                          )}
                          {!product.is_sold && !product.is_display_only && (
                            <Badge className="bg-green-600 text-white font-bold shadow text-[10px] px-1.5 py-0" data-testid={`admin-available-badge-${product.id}`}>Available</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={`relative w-full overflow-hidden hbe-paper hbe-card-media admin-card-media flex items-center justify-center px-2 py-2 md:px-3 md:py-3 ${
                      adminCols === 3
                        ? 'aspect-[5/4] md:aspect-[5/3]'
                        : adminCols === 2
                        ? 'aspect-[5/4] md:aspect-[5/3]'
                        : 'aspect-[4/3] md:aspect-[5/3]'
                    }`}>
                      <img 
                        src={product.image_url} 
                        alt={product.title}
                        className="relative z-[1] max-h-full max-w-full w-auto h-auto object-contain cursor-pointer transition-transform hover:scale-105"
                        onClick={() => window.open(product.image_url, '_blank')}
                      />
                      {!product.is_available && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <Badge className="bg-red-500 text-white">
                            <EyeOff className="h-3 w-3 mr-1" />
                            Hidden
                          </Badge>
                        </div>
                      )}
                    </div>
                    
                    {/* Image Gallery Thumbnails */}
                    {allImages.length > 1 && (
                      <div className="px-2 sm:px-4 pt-1 sm:pt-2">
                        <div className="flex space-x-2 overflow-x-auto">
                          {allImages.slice(0, 4).map((imageUrl, index) => (
                            <img
                              key={index}
                              src={imageUrl}
                              alt={`${product.title} - ${index + 1}`}
                              className="h-8 w-8 sm:h-12 sm:w-12 object-contain rounded cursor-pointer flex-shrink-0 border-2 border-transparent hover:border-blue-500"
                              onClick={() => window.open(imageUrl, '_blank')}
                            />
                          ))}
                          {allImages.length > 4 && (
                            <div className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center text-xs font-medium">
                              +{allImages.length - 4}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  <CardHeader className="p-2 sm:p-6">
                    <CardTitle 
                      className="text-sm sm:text-lg leading-tight line-clamp-2"
                      style={{ color: siteSettings?.text_color || '#92400e' }}
                    >
                      {product.title}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.8 }}>
                      {product.category} • ${product.price}
                      {product.discount_percentage > 0 && (
                        <Badge className="ml-2 bg-green-100 text-green-800">
                          ${product.discount_percentage} OFF
                        </Badge>
                      )}
                      {product.paid_price != null && (
                        <div className="mt-1 text-xs" style={{ color: '#7a6242' }}>
                          Paid: <strong>${Number(product.paid_price).toFixed(2)}</strong>
                          {product.price > 0 && (
                            <> · Profit: <strong style={{ color: (product.price - product.paid_price) >= 0 ? '#16a34a' : '#dc2626' }}>
                              ${(product.price - product.paid_price).toFixed(2)}
                            </strong></>
                          )}
                        </div>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-2 sm:p-6 pt-0">
                    <div className="flex flex-wrap justify-between items-center gap-2 mb-2 sm:mb-3">
                      <Button 
                        onClick={() => handleToggleAvailability(product.id, product.is_available)}
                        variant="outline" 
                        size="sm"
                        className="h-10 px-3 flex-1 sm:flex-none"
                        style={{ 
                          borderColor: product.is_available ? siteSettings?.border_color || '#fbbf24' : '#10b981',
                          color: product.is_available ? siteSettings?.text_color || '#92400e' : '#10b981'
                        }}
                        data-testid={`admin-toggle-availability-${product.id}`}
                      >
                        {product.is_available ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                        {product.is_available ? 'Hide' : 'Show'}
                      </Button>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => editProduct(product)}
                          variant="outline" 
                          size="sm"
                          className="h-10 w-10 sm:w-auto sm:px-3"
                          aria-label="Edit product"
                          style={{ 
                            borderColor: siteSettings?.border_color || '#fbbf24',
                            color: siteSettings?.text_color || '#92400e'
                          }}
                          data-testid={`admin-edit-product-${product.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          onClick={() => handleDeleteProduct(product.id)}
                          variant="outline" 
                          size="sm"
                          className="h-10 w-10 sm:w-auto sm:px-3 border-red-300 text-red-600 hover:bg-red-50"
                          aria-label="Delete product"
                          data-testid={`admin-delete-product-${product.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>

            {/* Add/Edit Product Dialog */}
            <Dialog open={isAddingProduct} onOpenChange={setIsAddingProduct}>
              <DialogTrigger asChild>
                <div style={{ display: 'none' }} />
              </DialogTrigger>
              <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                <DialogHeader>
                  <DialogTitle>{selectedProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                  <DialogDescription>
                    {selectedProduct ? 'Update product information' : 'Add a new product to your collection'}
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleProductSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Title *</Label>
                      <AutocompleteInput
                        id="title"
                        value={productForm.title}
                        onChange={(v) => setProductForm({...productForm, title: v})}
                        suggestions={titleSuggestions}
                        required
                        placeholder="1973 $1 Bank of Canada Note"
                        testId="admin-product-title"
                      />
                    </div>
                    <div>
                      <Label htmlFor="category">Category *</Label>
                      <Select 
                        value={productForm.category} 
                        onValueChange={(value) => setProductForm({...productForm, category: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoriesList.map(category => (
                            <SelectItem key={category.id} value={category.name}>{category.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={productForm.description}
                      onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                      rows={3}
                      required
                      placeholder="Detailed description of the item..."
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="price">Price ($) *</Label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm pointer-events-none" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.7 }}>$</span>
                        <Input
                          id="price"
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={productForm.price}
                          onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                          required
                          placeholder="149.99"
                          className="pl-6 no-spin"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="paid_price">Paid ($) <span className="text-xs text-amber-600">(admin only)</span></Label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm pointer-events-none" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.7 }}>$</span>
                        <Input
                          id="paid_price"
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={productForm.paid_price}
                          onChange={(e) => setProductForm({...productForm, paid_price: e.target.value})}
                          placeholder="What you paid"
                          data-testid="admin-paid-price-input"
                          className="pl-6 no-spin"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="discount">Discount ($ off)</Label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm pointer-events-none" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.7 }}>$</span>
                        <Input
                          id="discount"
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={productForm.discount_percentage}
                          onChange={(e) => setProductForm({...productForm, discount_percentage: e.target.value})}
                          placeholder="10"
                          className="pl-6 no-spin"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="year">Year</Label>
                      <Input
                        id="year"
                        type="number"
                        inputMode="numeric"
                        value={productForm.year}
                        onChange={(e) => setProductForm({...productForm, year: e.target.value})}
                        placeholder="1973"
                        className="no-spin"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="grade">Grade</Label>
                      <AutocompleteInput
                        id="grade"
                        value={productForm.grade}
                        onChange={(v) => setProductForm({...productForm, grade: v})}
                        suggestions={gradeSuggestions}
                        placeholder="Enter grade (e.g., PMG 70 Gem Unc EPQ, PCGS 69 PPQ, MS-65)"
                        testId="admin-product-grade"
                      />
                    </div>
                    <div>
                      <Label htmlFor="denomination">Denomination</Label>
                      <AutocompleteInput
                        id="denomination"
                        value={productForm.denomination}
                        onChange={(v) => setProductForm({...productForm, denomination: v})}
                        suggestions={denominationSuggestions}
                        placeholder="$1"
                        testId="admin-product-denomination"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <AutocompleteInput
                        id="country"
                        value={productForm.country}
                        onChange={(v) => setProductForm({...productForm, country: v})}
                        suggestions={countrySuggestions}
                        placeholder="Type a country (e.g., Canada, Cayman Islands)"
                        testId="admin-product-country"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Type freely to add a new country, or pick from suggestions.
                      </p>
                    </div>
                  </div>

                  {/* Product Images — numbered slots (1 = main) */}
                  <div data-testid="admin-image-slots">
                    <Label>Product Images</Label>
                    <p className="text-xs text-amber-700 mt-1 mb-2">
                      Tap a slot to set or replace that photo. Slot 1 is the main photo. Clear (×) works on mobile — no hover needed.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {(() => {
                        const filledCount = (productForm.image_url ? 1 : 0) + (productForm.image_urls || []).length;
                        // Always show at least 4 slots, plus one empty after the last filled
                        const slotCount = Math.max(4, filledCount + 1);
                        return Array.from({ length: slotCount }, (_, slotIndex) => {
                          const url = getImageAtSlot(productForm, slotIndex);
                          const label = slotIndex === 0 ? '1 · Main' : String(slotIndex + 1);
                          return (
                            <div
                              key={slotIndex}
                              className="relative aspect-square rounded-lg border-2 border-dashed bg-white/80 overflow-hidden"
                              style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}
                              data-testid={`admin-image-slot-${slotIndex}`}
                            >
                              {url ? (
                                <img
                                  src={url}
                                  alt={`Slot ${slotIndex + 1}`}
                                  className="absolute inset-0 h-full w-full object-cover"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-amber-800/70 p-2 text-center pointer-events-none">
                                  <Upload className="h-5 w-5 mb-1" />
                                  <span className="text-xs font-medium">{label}</span>
                                  <span className="text-[10px] mt-0.5">Tap to add</span>
                                </div>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                aria-label={`Upload image for slot ${slotIndex + 1}`}
                                onChange={(e) => {
                                  openCropForFiles(`slot:${slotIndex}`, e.target.files);
                                  e.target.value = '';
                                }}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                disabled={uploadingImage}
                              />
                              {url && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    clearImageSlot(slotIndex);
                                  }}
                                  className="absolute top-1 right-1 z-20 h-8 w-8 rounded-full bg-red-500 text-white text-lg font-bold flex items-center justify-center shadow-md"
                                  aria-label={`Clear slot ${slotIndex + 1}`}
                                  data-testid={`admin-image-slot-clear-${slotIndex}`}
                                >
                                  ×
                                </button>
                              )}
                              {url && (
                                <span className="absolute bottom-1 left-1 z-20 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-semibold pointer-events-none">
                                  {label}
                                </span>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      Total images: {(productForm.image_url ? 1 : 0) + (productForm.image_urls || []).length}
                      {uploadingImage && uploadProgress.total > 0
                        ? ` · Uploading ${uploadProgress.current}/${uploadProgress.total}…`
                        : ''}
                    </p>
                    {/* Optional paste-URL for slot 1 if needed */}
                    <div className="mt-2">
                      <Input
                        id="image_url"
                        value={productForm.image_url}
                        onChange={(e) => setProductForm({...productForm, image_url: e.target.value})}
                        placeholder="Or paste main image URL here"
                        className="text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_available"
                      checked={productForm.is_available}
                      onChange={(e) => setProductForm({...productForm, is_available: e.target.checked})}
                    />
                    <Label htmlFor="is_available">Product is available for sale</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_sold"
                      checked={productForm.is_sold || false}
                      onChange={(e) => setProductForm({...productForm, is_sold: e.target.checked})}
                    />
                    <Label htmlFor="is_sold">Mark as SOLD (still displays with SOLD overlay)</Label>
                  </div>

                  <div className="rounded-md border p-3 space-y-2" style={{ borderColor: siteSettings?.border_color || '#fbbf24', background: '#eff6ff' }}>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is_display_only"
                        checked={productForm.is_display_only === true}
                        onChange={(e) => setProductForm({...productForm, is_display_only: e.target.checked})}
                        data-testid="admin-product-is-display-only"
                      />
                      <Label htmlFor="is_display_only">Display Only — Personal Collection (disables Add to Cart)</Label>
                    </div>
                    <p className="text-xs" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.75 }}>
                      Shows a blue “Collection” tag on the product card and hides the buy button.
                    </p>
                  </div>

                  <div className="rounded-md border p-3 space-y-2" style={{ borderColor: siteSettings?.border_color || '#fbbf24', background: '#fffbeb' }}>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="allow_offers"
                        checked={productForm.allow_offers === true}
                        onChange={(e) => setProductForm({...productForm, allow_offers: e.target.checked})}
                        data-testid="admin-product-allow-offers"
                      />
                      <Label htmlFor="allow_offers">Allow customers to submit offers on this item</Label>
                    </div>
                    {productForm.allow_offers === true && (
                      <div>
                        <Label htmlFor="min_offer_amount">Minimum offer ($) — required</Label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm pointer-events-none" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.7 }}>$</span>
                          <Input
                            id="min_offer_amount"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Buyers cannot submit less than this"
                            value={productForm.min_offer_amount}
                            onChange={(e) => setProductForm({...productForm, min_offer_amount: e.target.value})}
                            className="pl-6 no-spin"
                            data-testid="admin-product-min-offer"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="rounded-md border p-3 space-y-2" style={{ borderColor: siteSettings?.border_color || '#fbbf24', background: '#fffbeb' }}>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is_featured"
                        checked={productForm.is_featured === true}
                        onChange={(e) => setProductForm({...productForm, is_featured: e.target.checked})}
                        data-testid="admin-product-is-featured"
                      />
                      <Label htmlFor="is_featured">Show in Featured Products on the main site</Label>
                    </div>
                    {productForm.is_featured === true && (
                      <div>
                        <Label htmlFor="featured_order">Featured order (lower number shows first)</Label>
                        <Input
                          id="featured_order"
                          type="number"
                          step="1"
                          min="0"
                          placeholder="e.g. 1, 2, 3…"
                          value={productForm.featured_order}
                          onChange={(e) => setProductForm({...productForm, featured_order: e.target.value})}
                          className="no-spin"
                          data-testid="admin-product-featured-order"
                        />
                        <p className="text-xs mt-1" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.7 }}>
                          Leave blank to let it appear after items that have an order set.
                        </p>
                      </div>
                    )}
                  </div>

                  <DialogFooter className="flex-col sm:flex-row gap-2">
                    <Button type="button" variant="outline" onClick={() => {setIsAddingProduct(false); resetProductForm();}} className="w-full sm:w-auto">
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-amber-600 hover:bg-amber-700 w-full sm:w-auto">
                      <Save className="h-4 w-4 mr-2" />
                      {selectedProduct ? 'Update Product' : 'Add Product'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Appearance Tab */}
          <TabsContent value="appearance" className="space-y-6">
            <h2 
              className="text-2xl font-bold font-serif"
              style={{ color: siteSettings?.text_color || '#92400e' }}
            >
              Website Appearance
            </h2>
            
            <Card 
              className="bg-white/90 backdrop-blur-sm"
              style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}
            >
              <CardHeader>
                <CardTitle 
                  className="flex items-center space-x-2"
                  style={{ color: siteSettings?.text_color || '#92400e' }}
                >
                  <Paintbrush className="h-5 w-5" />
                  <span>Complete Visual Customization</span>
                </CardTitle>
                <CardDescription style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.8 }}>
                  Customize every aspect of your website&apos;s appearance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSettingsSubmit} className="space-y-6">
                  
                  {/* Site Branding */}
                  <div 
                    className="border rounded-lg p-4 space-y-4"
                    style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}
                  >
                    <h3 
                      className="text-lg font-semibold"
                      style={{ color: siteSettings?.text_color || '#92400e' }}
                    >
                      Site Branding
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="site_title">Site Title</Label>
                        <Input
                          id="site_title"
                          value={settingsForm.site_title}
                          onChange={(e) => setSettingsForm({...settingsForm, site_title: e.target.value})}
                          placeholder="Historical Bill Exchange"
                        />
                      </div>
                      <div>
                        <Label htmlFor="site_subtitle">Site Subtitle</Label>
                        <Input
                          id="site_subtitle"
                          value={settingsForm.site_subtitle}
                          onChange={(e) => setSettingsForm({...settingsForm, site_subtitle: e.target.value})}
                          placeholder="Premium Collectible Currency"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="hero_tagline">Homepage Tagline (under the title)</Label>
                      <Textarea
                        id="hero_tagline"
                        value={settingsForm.hero_tagline}
                        onChange={(e) => setSettingsForm({...settingsForm, hero_tagline: e.target.value})}
                        rows={3}
                        placeholder="Discover rare, collectible banknotes and coins…"
                        data-testid="admin-hero-tagline-input"
                      />
                      <p className="text-xs text-amber-700 mt-1">Line breaks are preserved. Shown on the homepage right under the title.</p>
                    </div>

                    {/* Side banners */}
                    <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/50 space-y-3">
                      <div>
                        <Label className="text-base font-semibold">Side Banners (desktop only)</Label>
                        <p className="text-xs text-amber-700 mt-1">Adds vertical accent images down the left/right edges of the homepage. Hidden on tablet/mobile.</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="side_left">Left Banner Image</Label>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Input
                              id="side_left"
                              value={settingsForm.side_banner_left_url}
                              onChange={(e) => setSettingsForm({...settingsForm, side_banner_left_url: e.target.value})}
                              placeholder="Image URL, or use upload"
                            />
                            <div className="relative w-full sm:w-auto">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => { openCropForFiles('side_left', e.target.files); e.target.value = ''; }}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                disabled={uploadingImage}
                              />
                              <Button type="button" variant="outline" disabled={uploadingImage} className="w-full sm:w-auto">
                                <Upload className="h-4 w-4 mr-2" />Upload
                              </Button>
                            </div>
                          </div>
                          {settingsForm.side_banner_left_url && (
                            <div className="mt-2 flex items-center gap-2">
                              <img src={settingsForm.side_banner_left_url} alt="Left banner preview" className="h-24 w-16 object-cover rounded border" />
                              <button type="button" onClick={() => setSettingsForm({...settingsForm, side_banner_left_url: ''})} className="text-xs text-red-600 underline">Remove</button>
                            </div>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="side_right">Right Banner Image</Label>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Input
                              id="side_right"
                              value={settingsForm.side_banner_right_url}
                              onChange={(e) => setSettingsForm({...settingsForm, side_banner_right_url: e.target.value})}
                              placeholder="Image URL, or use upload"
                            />
                            <div className="relative w-full sm:w-auto">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => { openCropForFiles('side_right', e.target.files); e.target.value = ''; }}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                disabled={uploadingImage}
                              />
                              <Button type="button" variant="outline" disabled={uploadingImage} className="w-full sm:w-auto">
                                <Upload className="h-4 w-4 mr-2" />Upload
                              </Button>
                            </div>
                          </div>
                          {settingsForm.side_banner_right_url && (
                            <div className="mt-2 flex items-center gap-2">
                              <img src={settingsForm.side_banner_right_url} alt="Right banner preview" className="h-24 w-16 object-cover rounded border" />
                              <button type="button" onClick={() => setSettingsForm({...settingsForm, side_banner_right_url: ''})} className="text-xs text-red-600 underline">Remove</button>
                            </div>
                          )}
                        </div>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settingsForm.side_banner_fade}
                          onChange={(e) => setSettingsForm({...settingsForm, side_banner_fade: e.target.checked})}
                          className="h-4 w-4 text-amber-600 rounded focus:ring-amber-500"
                        />
                        <span className="text-sm text-amber-900">Fade edges into background (gradient blend)</span>
                      </label>
                    </div>
                    
                    <div>
                      <Label htmlFor="logo_url">Custom Logo (JPG/PNG recommended)</Label>
                      <div className="flex space-x-2">
                        <Input
                          id="logo_url"
                          value={settingsForm.logo_url}
                          onChange={(e) => setSettingsForm({...settingsForm, logo_url: e.target.value})}
                          placeholder="Upload JPG/PNG logo or enter URL"
                          className="flex-1"
                        />
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                console.log('Logo selected:', file.name, file.type);
                                handleImageUpload(file, (url) => {
                                  console.log('Logo uploaded:', url);
                                  setSettingsForm({...settingsForm, logo_url: url});
                                });
                              }
                            }}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            disabled={uploadingImage}
                          />
                          <Button type="button" variant="outline" disabled={uploadingImage}>
                            <Upload className="h-4 w-4" />
                            {uploadingImage && <span className="ml-2">Uploading...</span>}
                          </Button>
                        </div>
                      </div>
                      {settingsForm.logo_url && (
                        <div className="mt-2 p-2 border rounded bg-gray-50">
                          <img 
                            src={settingsForm.logo_url} 
                            alt="Logo Preview" 
                            className="h-16 w-auto object-contain max-w-[200px]"
                            onError={(e) => {
                              console.error('Logo preview failed to load:', e.target.src);
                              e.target.style.display = 'none';
                            }}
                          />
                          <p className="text-xs text-gray-600 mt-1">Logo Preview (max height: 48px on site)</p>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="announcement_text">Scrolling top banner (marquee)</Label>
                      <Textarea
                        id="announcement_text"
                        value={settingsForm.announcement_text}
                        onChange={(e) => setSettingsForm({...settingsForm, announcement_text: e.target.value})}
                        placeholder="Banner text (defaults are pre-filled: Authenticated notes — Graded… — Email Historic.Bills@gmail.com…). Separate statements with a newline or ' — '."
                        rows={3}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Edit the moving banner under the header. Separate statements with new lines or dashes ( — ). Emails become clickable. Field is pre-filled with the current banner text.</p>
                      <div className="flex items-center space-x-2 mt-2">
                        <input
                          type="checkbox"
                          id="announcement_enabled"
                          checked={settingsForm.announcement_enabled}
                          onChange={(e) => setSettingsForm({...settingsForm, announcement_enabled: e.target.checked})}
                        />
                        <Label htmlFor="announcement_enabled">Show scrolling top banner</Label>
                      </div>
                    </div>
                  </div>

                  {/* Color Scheme */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <h3 className="text-lg font-semibold text-amber-900">Color Scheme</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="primary_color">Primary Color (Buttons, Logo Background)</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="primary_color"
                            type="color"
                            value={settingsForm.primary_color}
                            onChange={(e) => setSettingsForm({...settingsForm, primary_color: e.target.value})}
                            className="w-16 h-10"
                          />
                          <Input
                            value={settingsForm.primary_color}
                            onChange={(e) => setSettingsForm({...settingsForm, primary_color: e.target.value})}
                            placeholder="#d97706"
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="secondary_color">Secondary Color (Hover States, Gradients)</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="secondary_color"
                            type="color"
                            value={settingsForm.secondary_color}
                            onChange={(e) => setSettingsForm({...settingsForm, secondary_color: e.target.value})}
                            className="w-16 h-10"
                          />
                          <Input
                            value={settingsForm.secondary_color}
                            onChange={(e) => setSettingsForm({...settingsForm, secondary_color: e.target.value})}
                            placeholder="#b45309"
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="accent_color">Accent Color (Badges, Highlights)</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="accent_color"
                            type="color"
                            value={settingsForm.accent_color}
                            onChange={(e) => setSettingsForm({...settingsForm, accent_color: e.target.value})}
                            className="w-16 h-10"
                          />
                          <Input
                            value={settingsForm.accent_color}
                            onChange={(e) => setSettingsForm({...settingsForm, accent_color: e.target.value})}
                            placeholder="#f59e0b"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="text_color">Text Color</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="text_color"
                            type="color"
                            value={settingsForm.text_color}
                            onChange={(e) => setSettingsForm({...settingsForm, text_color: e.target.value})}
                            className="w-16 h-10"
                          />
                          <Input
                            value={settingsForm.text_color}
                            onChange={(e) => setSettingsForm({...settingsForm, text_color: e.target.value})}
                            placeholder="#92400e"
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="border_color">Border Color</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="border_color"
                            type="color"
                            value={settingsForm.border_color}
                            onChange={(e) => setSettingsForm({...settingsForm, border_color: e.target.value})}
                            className="w-16 h-10"
                          />
                          <Input
                            value={settingsForm.border_color}
                            onChange={(e) => setSettingsForm({...settingsForm, border_color: e.target.value})}
                            placeholder="#fbbf24"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="header_color">Header/Top Color</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="header_color"
                            type="color"
                            value={settingsForm.header_color || '#ffffff'}
                            onChange={(e) => setSettingsForm({...settingsForm, header_color: e.target.value})}
                            className="w-16 h-10"
                          />
                          <Input
                            value={settingsForm.header_color || '#ffffff'}
                            onChange={(e) => setSettingsForm({...settingsForm, header_color: e.target.value})}
                            placeholder="#ffffff"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Background Settings */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <h3 className="text-lg font-semibold text-amber-900">Background Settings</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="background_type">Background Type</Label>
                        <Select 
                          value={settingsForm.background_type} 
                          onValueChange={(value) => setSettingsForm({...settingsForm, background_type: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="solid">Solid Color</SelectItem>
                            <SelectItem value="gradient">Gradient</SelectItem>
                            <SelectItem value="image">Background Image</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="background_overlay"
                          checked={settingsForm.background_overlay}
                          onChange={(e) => setSettingsForm({...settingsForm, background_overlay: e.target.checked})}
                        />
                        <Label htmlFor="background_overlay">Enable soft overlay/fade effect</Label>
                      </div>
                    </div>
                    
                    {/* Solid Color Settings */}
                    {settingsForm.background_type === 'solid' && (
                      <div>
                        <Label htmlFor="background_solid_color">Background Color</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="background_solid_color"
                            type="color"
                            value={settingsForm.background_solid_color}
                            onChange={(e) => setSettingsForm({...settingsForm, background_solid_color: e.target.value})}
                            className="w-16 h-10"
                          />
                          <Input
                            value={settingsForm.background_solid_color}
                            onChange={(e) => setSettingsForm({...settingsForm, background_solid_color: e.target.value})}
                            placeholder="#ffffff"
                            className="flex-1"
                          />
                        </div>
                        <div 
                          className="mt-2 h-16 w-full rounded border"
                          style={{ backgroundColor: settingsForm.background_solid_color }}
                        ></div>
                      </div>
                    )}
                    
                    {/* Gradient Settings */}
                    {settingsForm.background_type === 'gradient' && (
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="gradient_direction">Gradient Direction</Label>
                          <Select 
                            value={settingsForm.gradient_direction} 
                            onValueChange={(value) => setSettingsForm({...settingsForm, gradient_direction: value})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="90deg">Top to Bottom</SelectItem>
                              <SelectItem value="180deg">Bottom to Top</SelectItem>
                              <SelectItem value="0deg">Left to Right</SelectItem>
                              <SelectItem value="270deg">Right to Left</SelectItem>
                              <SelectItem value="45deg">Diagonal (↗)</SelectItem>
                              <SelectItem value="135deg">Diagonal (↘)</SelectItem>
                              <SelectItem value="225deg">Diagonal (↙)</SelectItem>
                              <SelectItem value="315deg">Diagonal (↖)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="gradient_color1">Start Color</Label>
                            <div className="flex space-x-2">
                              <Input
                                id="gradient_color1"
                                type="color"
                                value={settingsForm.gradient_color1}
                                onChange={(e) => setSettingsForm({...settingsForm, gradient_color1: e.target.value})}
                                className="w-16 h-10"
                              />
                              <Input
                                value={settingsForm.gradient_color1}
                                onChange={(e) => setSettingsForm({...settingsForm, gradient_color1: e.target.value})}
                                placeholder="#fef7e0"
                                className="flex-1"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label htmlFor="gradient_color2">Middle Color</Label>
                            <div className="flex space-x-2">
                              <Input
                                id="gradient_color2"
                                type="color"
                                value={settingsForm.gradient_color2}
                                onChange={(e) => setSettingsForm({...settingsForm, gradient_color2: e.target.value})}
                                className="w-16 h-10"
                              />
                              <Input
                                value={settingsForm.gradient_color2}
                                onChange={(e) => setSettingsForm({...settingsForm, gradient_color2: e.target.value})}
                                placeholder="#ffffff"
                                className="flex-1"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label htmlFor="gradient_color3">End Color</Label>
                            <div className="flex space-x-2">
                              <Input
                                id="gradient_color3"
                                type="color"
                                value={settingsForm.gradient_color3}
                                onChange={(e) => setSettingsForm({...settingsForm, gradient_color3: e.target.value})}
                                className="w-16 h-10"
                              />
                              <Input
                                value={settingsForm.gradient_color3}
                                onChange={(e) => setSettingsForm({...settingsForm, gradient_color3: e.target.value})}
                                placeholder="#fff7ed"
                                className="flex-1"
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Gradient Preview */}
                        <div>
                          <Label>Gradient Preview</Label>
                          <div 
                            className="mt-2 h-16 w-full rounded border"
                            style={{ 
                              background: `linear-gradient(${settingsForm.gradient_direction}, ${settingsForm.gradient_color1} 0%, ${settingsForm.gradient_color2} 50%, ${settingsForm.gradient_color3} 100%)` 
                            }}
                          ></div>
                        </div>
                      </div>
                    )}
                    
                    {/* Image Background Settings */}
                    {settingsForm.background_type === 'image' && (
                      <div>
                        <Label htmlFor="background_value">Background Image URL</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="background_value"
                            value={settingsForm.background_value}
                            onChange={(e) => setSettingsForm({...settingsForm, background_value: e.target.value})}
                            placeholder="https://example.com/background.jpg"
                            className="flex-1"
                          />
                          <div className="relative">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e.target.files[0], (url) => setSettingsForm({...settingsForm, background_value: url}))}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              disabled={uploadingImage}
                            />
                            <Button type="button" variant="outline" disabled={uploadingImage}>
                              <Upload className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        {settingsForm.background_value && (
                          <img src={settingsForm.background_value} alt="Background Preview" className="mt-2 h-20 w-32 object-cover rounded" />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="pt-4">
                    <Button type="submit" className="bg-amber-600 hover:bg-amber-700 w-full">
                      <Save className="h-4 w-4 mr-2" />
                      Save All Appearance Settings
                    </Button>
                    <p className="text-sm text-amber-600 mt-2 text-center">
                      Changes will be applied immediately after saving
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-6">
            <h2 
              className="text-2xl font-bold font-serif"
              style={{ color: siteSettings?.text_color || '#92400e' }}
            >
              Manage Categories
            </h2>
            <p className="text-sm" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.75 }}>
              Add, rename, delete, and reorder the categories that appear on the homepage and product filter.
            </p>

            {/* Add new category */}
            <Card className="bg-white/90 backdrop-blur-sm" style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr,1.5fr,auto] gap-2 items-end">
                  <div>
                    <Label>New Category Name</Label>
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="e.g. Gold Coins"
                      data-testid="admin-new-category-name"
                    />
                  </div>
                  <div>
                    <Label>Description (optional)</Label>
                    <Input
                      value={newCategoryDesc}
                      onChange={(e) => setNewCategoryDesc(e.target.value)}
                      placeholder="Shown on homepage tile"
                      data-testid="admin-new-category-desc"
                    />
                  </div>
                  <Button
                    onClick={handleAddCategory}
                    className="text-white"
                    style={{ backgroundColor: siteSettings?.primary_color || '#d97706' }}
                    data-testid="admin-add-category-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Existing categories list */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoriesList.map((cat, index) => (
                <Card 
                  key={cat.id} 
                  className="bg-white/90 backdrop-blur-sm"
                  style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}
                  data-testid={`admin-category-card-${cat.id}`}
                >
                  <div className="relative h-40 overflow-hidden rounded-t-lg">
                    <img 
                      src={getCategoryImage(cat.name)} 
                      alt={cat.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1580519542036-c47de6196ba5?w=600&q=70';
                      }}
                    />
                  </div>
                  <CardHeader className="pb-2">
                    {editingCategoryId === cat.id ? (
                      <div className="space-y-2">
                        <Input
                          value={editingCategoryName}
                          onChange={(e) => setEditingCategoryName(e.target.value)}
                          placeholder="Category name"
                          data-testid={`admin-category-edit-name-${cat.id}`}
                        />
                        <Input
                          value={editingCategoryDesc}
                          onChange={(e) => setEditingCategoryDesc(e.target.value)}
                          placeholder="Description"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleSaveCategoryEdit} className="text-white flex-1" style={{ backgroundColor: siteSettings?.primary_color || '#d97706' }} data-testid={`admin-category-save-${cat.id}`}>
                            <Save className="h-3 w-3 mr-1" /> Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingCategoryId(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <CardTitle className="flex items-center justify-between text-base" style={{ color: siteSettings?.text_color || '#92400e' }}>
                          <span className="truncate">{cat.name}</span>
                          <div className="flex gap-1 shrink-0">
                            <button
                              onClick={() => handleReorderCategory(index, -1)}
                              disabled={index === 0}
                              className="p-1 rounded hover:bg-amber-100 disabled:opacity-30"
                              title="Move up"
                              data-testid={`admin-category-up-${cat.id}`}
                            >
                              ▲
                            </button>
                            <button
                              onClick={() => handleReorderCategory(index, 1)}
                              disabled={index === categoriesList.length - 1}
                              className="p-1 rounded hover:bg-amber-100 disabled:opacity-30"
                              title="Move down"
                              data-testid={`admin-category-down-${cat.id}`}
                            >
                              ▼
                            </button>
                          </div>
                        </CardTitle>
                        {cat.description && (
                          <p className="text-xs" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.7 }}>{cat.description}</p>
                        )}
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingCategoryId(cat.id);
                              setEditingCategoryName(cat.name);
                              setEditingCategoryDesc(cat.description || '');
                            }}
                            data-testid={`admin-category-edit-${cat.id}`}
                          >
                            <Edit className="h-3 w-3 mr-1" /> Rename
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteCategory(cat.id, cat.name)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`admin-category-delete-${cat.id}`}
                          >
                            <Trash2 className="h-3 w-3 mr-1" /> Delete
                          </Button>
                        </div>
                      </>
                    )}
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      <Label className="text-xs" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.75 }}>Category image</Label>
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleImageUpload(e.target.files[0], (url) => {
                                handleCategoryImageUpdate(cat.name, url);
                              });
                            }
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          disabled={uploadingImage}
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="w-full" 
                          disabled={uploadingImage}
                          style={{ 
                            borderColor: siteSettings?.border_color || '#fbbf24',
                            color: siteSettings?.text_color || '#92400e'
                          }}
                        >
                          <FileImage className="h-3 w-3 mr-1" />
                          {uploadingImage ? 'Uploading...' : 'Upload Image'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Grades Tab Removed - Users can type grades directly in product form */}

          <TabsContent value="silver" className="space-y-6" data-testid="admin-silver-section">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold font-serif" style={{ color: siteSettings?.text_color || '#92400e' }}>
                  Silver Holdings
                </h2>
                <p className="text-sm" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.75 }}>
                  Track silver oz, year, paid & sold. Profits here are separate from bill catalog P&L.
                </p>
              </div>
              <Button variant="outline" onClick={fetchSilver} disabled={silverLoading} data-testid="admin-silver-refresh">
                {silverLoading ? 'Loading…' : 'Refresh'}
              </Button>
            </div>

            {silverSummary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ['Total oz', silverSummary.total_oz],
                  ['Paid (CAD)', `$${formatMoney(silverSummary.total_paid)}`],
                  ['Sold (CAD)', `$${formatMoney(silverSummary.total_sold)}`],
                  ['Realized profit', `$${formatMoney(silverSummary.realized_profit)}`],
                ].map(([label, val]) => (
                  <Card key={label} className="bg-white/90" style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}>
                    <CardContent className="p-3">
                      <div className="text-xs opacity-70" style={{ color: siteSettings?.text_color || '#92400e' }}>{label}</div>
                      <div className="text-lg font-semibold" style={{ color: siteSettings?.text_color || '#92400e' }}>{val}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <Card className="bg-white/90" style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}>
              <CardHeader>
                <CardTitle style={{ color: siteSettings?.text_color || '#92400e' }}>
                  {editingSilverId ? 'Edit silver holding' : 'Add silver holding'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSilverSubmit} className="space-y-3" data-testid="admin-silver-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div>
                      <Label>Description</Label>
                      <Input value={silverForm.description} onChange={(e) => setSilverForm({ ...silverForm, description: e.target.value })} placeholder="e.g. Maple Leaf 1oz" data-testid="admin-silver-description" />
                    </div>
                    <div>
                      <Label>Silver oz</Label>
                      <Input type="number" step="0.0001" value={silverForm.silver_oz} onChange={(e) => setSilverForm({ ...silverForm, silver_oz: e.target.value })} placeholder="1" required data-testid="admin-silver-oz" />
                    </div>
                    <div>
                      <Label>Year</Label>
                      <Input value={silverForm.year} onChange={(e) => setSilverForm({ ...silverForm, year: e.target.value })} placeholder="2024" data-testid="admin-silver-year" />
                    </div>
                    
                    <div>
                      <Label>Price paid (CAD)</Label>
                      <Input type="number" step="0.01" value={silverForm.price_paid} onChange={(e) => setSilverForm({ ...silverForm, price_paid: e.target.value })} placeholder="0.00" required data-testid="admin-silver-paid" />
                    </div>
                    <div>
                      <Label>Price sold (CAD)</Label>
                      <Input type="number" step="0.01" value={silverForm.price_sold} onChange={(e) => setSilverForm({ ...silverForm, price_sold: e.target.value })} placeholder="leave blank if unsold" data-testid="admin-silver-sold" />
                    </div>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Input value={silverForm.notes} onChange={(e) => setSilverForm({ ...silverForm, notes: e.target.value })} placeholder="Optional" data-testid="admin-silver-notes" />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button type="submit" className="text-white" style={{ backgroundColor: siteSettings?.primary_color || '#d97706' }} data-testid="admin-silver-save">
                      <Save className="h-4 w-4 mr-1" /> {editingSilverId ? 'Update' : 'Add'}
                    </Button>
                    {editingSilverId && (
                      <Button type="button" variant="outline" onClick={resetSilverForm}>Cancel edit</Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <h3 className="text-lg font-semibold" style={{ color: siteSettings?.text_color || '#92400e' }}>
                Holdings ({silverHoldings.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {silverHoldings.map((row) => {
                  const profit = row.price_sold != null && row.price_sold !== ''
                    ? (Number(row.price_sold) - Number(row.price_paid || 0))
                    : null;
                  return (
                    <Card key={row.id} className="bg-white/90" style={{ borderColor: siteSettings?.border_color || '#fbbf24' }} data-testid={`admin-silver-row-${row.id}`}>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex justify-between gap-2 items-start">
                          <div>
                            <div className="font-semibold" style={{ color: siteSettings?.text_color || '#92400e' }}>{row.description || '(no description)'}</div>
                            <div className="text-xs opacity-70" style={{ color: siteSettings?.text_color || '#92400e' }}>
                              {row.silver_oz} oz · {row.year || '—'}
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button size="sm" variant="outline" onClick={() => handleEditSilver(row)}><Edit className="h-3 w-3" /></Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteSilver(row.id)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm" style={{ color: siteSettings?.text_color || '#92400e' }}>
                          <div><span className="opacity-60">Paid</span><div className="font-medium">${formatMoney(row.price_paid)}</div></div>
                          <div><span className="opacity-60">Sold</span><div className="font-medium">{row.price_sold != null && row.price_sold !== '' ? `$${formatMoney(row.price_sold)}` : '—'}</div></div>
                          <div><span className="opacity-60">Profit</span><div className={`font-medium ${profit == null ? '' : profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>{profit == null ? 'open' : `$${formatMoney(profit)}`}</div></div>
                        </div>
                        {row.notes && <p className="text-xs opacity-70">{row.notes}</p>}
                      </CardContent>
                    </Card>
                  );
                })}
                {silverHoldings.length === 0 && !silverLoading && (
                  <p className="text-sm opacity-70" style={{ color: siteSettings?.text_color || '#92400e' }}>No silver holdings yet — add one above.</p>
                )}
              </div>
            </div>
          </TabsContent>

        </Tabs>
      </div>

      {/* Image crop modal (multi-file queue) */}
      <ImageCropModal
        open={cropQueue.length > 0}
        files={cropQueue}
        primaryColor={siteSettings?.primary_color || '#d97706'}
        onFinish={handleCropFinished}
        onCancel={() => { setCropQueue([]); setCropTarget(null); }}
      />

      {/* Bulk Paid Editor Dialog */}
      <Dialog open={showBulkPaidDialog} onOpenChange={setShowBulkPaidDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ color: siteSettings?.text_color || '#92400e' }}>Bulk Edit Prices</DialogTitle>
            <DialogDescription>
              Update sale price and what you paid for many products at once. Only changed rows are saved.
            </DialogDescription>
          </DialogHeader>

          <div className="pt-2 pb-3">
            <Input
              placeholder="Search by title, year, denomination..."
              value={bulkPaidSearch}
              onChange={(e) => setBulkPaidSearch(e.target.value)}
              data-testid="bulk-paid-search"
            />
          </div>

          <div className="flex-1 overflow-auto border rounded-lg" style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}>
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-amber-50 z-10">
                <tr className="border-b" style={{ borderColor: siteSettings?.border_color || '#fbbf24' }}>
                  <th className="text-left px-3 py-2" style={{ color: siteSettings?.text_color || '#92400e' }}>Product</th>
                  <th className="text-right px-3 py-2 w-36" style={{ color: siteSettings?.text_color || '#92400e' }}>Price</th>
                  <th className="text-right px-3 py-2 w-36" style={{ color: siteSettings?.text_color || '#92400e' }}>Paid</th>
                </tr>
              </thead>
              <tbody>
                {products
                  .filter(p => {
                    if (!bulkPaidSearch.trim()) return true;
                    const q = bulkPaidSearch.toLowerCase();
                    return [p.title, p.year, p.denomination, p.category, p.grade]
                      .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
                  })
                  .map(p => (
                    <tr key={p.id} className="border-b hover:bg-amber-50/50" style={{ borderColor: '#fef3c7' }} data-testid={`bulk-paid-row-${p.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium truncate max-w-[220px] sm:max-w-[280px]" style={{ color: siteSettings?.text_color || '#92400e' }}>{p.title}</div>
                      </td>
                      <td className="text-right px-3 py-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm pointer-events-none" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.7 }}>$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={bulkPriceValues[p.id] ?? ''}
                            onChange={(e) => setBulkPriceValues(prev => ({ ...prev, [p.id]: e.target.value }))}
                            className="text-right pl-6 no-spin"
                            data-testid={`bulk-price-input-${p.id}`}
                          />
                        </div>
                      </td>
                      <td className="text-right px-3 py-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm pointer-events-none" style={{ color: siteSettings?.text_color || '#92400e', opacity: 0.7 }}>$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={bulkPaidValues[p.id] ?? ''}
                            onChange={(e) => setBulkPaidValues(prev => ({ ...prev, [p.id]: e.target.value }))}
                            className="text-right pl-6 no-spin"
                            data-testid={`bulk-paid-input-${p.id}`}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <DialogFooter className="pt-3 flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowBulkPaidDialog(false)} className="w-full sm:w-auto">Cancel</Button>
            <Button
              onClick={async () => {
                setSavingBulkPaid(true);
                const updates = [];
                products.forEach(p => {
                  const paidRaw = bulkPaidValues[p.id];
                  const priceRaw = bulkPriceValues[p.id];
                  const originalPaid = p.paid_price != null ? String(p.paid_price) : '';
                  const originalPrice = p.price != null ? String(p.price) : '';
                  const paidChanged = (paidRaw ?? '') !== originalPaid;
                  const priceChanged = (priceRaw ?? '') !== originalPrice;
                  if (!paidChanged && !priceChanged) return;
                  const entry = { id: p.id };
                  if (paidChanged) {
                    entry.paid_price = (paidRaw === '' || paidRaw == null) ? null : parseFloat(paidRaw);
                  }
                  if (priceChanged) {
                    const parsed = parseFloat(priceRaw);
                    if (isNaN(parsed) || parsed < 0) return; // skip invalid
                    entry.price = parsed;
                  }
                  updates.push(entry);
                });
                if (updates.length === 0) {
                  toast.info('No changes to save.');
                  setSavingBulkPaid(false);
                  return;
                }
                try {
                  const res = await axios.post(`${API}/admin/products/bulk-paid`, { updates });
                  toast.success(`Updated ${res.data.updated} product(s)`);
                  await fetchProducts();
                  setShowBulkPaidDialog(false);
                } catch (e) {
                  toast.error('Failed to save changes');
                } finally {
                  setSavingBulkPaid(false);
                }
              }}
              disabled={savingBulkPaid}
              className="text-white w-full sm:w-auto"
              style={{ backgroundColor: siteSettings?.primary_color || '#d97706' }}
              data-testid="bulk-paid-save-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingBulkPaid ? 'Saving...' : 'Save All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminDashboard;